/**
 * DeadlinesTab — Tasks Reports Phase 2.
 *
 * KPI tiles for on-time / late / overdue + delay histogram + overdue task list.
 */
import React, { useMemo, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Target, TimerOff, Clock, AlertTriangle, CalendarX, CalendarCheck2, ShieldCheck,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const buildParams = (filters) => {
  const p = { from_date: filters.fromDate, to_date: filters.toDate };
  if (filters.assigneeId && filters.assigneeId !== 'all') p.assignee_id = filters.assigneeId;
  if (filters.clientId && filters.clientId !== 'all') p.client_id = filters.clientId;
  if (filters.department && filters.department !== 'all') p.department = filters.department;
  if (filters.taskType && filters.taskType !== 'all') p.task_type = filters.taskType;
  if (filters.subtype && filters.subtype !== 'all') p.subtype = filters.subtype;
  if (filters.priority && filters.priority !== 'all') p.priority = filters.priority;
  if (filters.search) p.search = filters.search;
  return p;
};

const Tile = ({ icon: Icon, label, value, suffix, hint, tone = 'neutral', testid }) => {
  const toneCls = {
    pink: 'text-pink-700', emerald: 'text-emerald-700', amber: 'text-amber-700',
    red: 'text-red-700', blue: 'text-blue-700', neutral: 'text-neutral-800',
  }[tone] || 'text-neutral-800';
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 hover:shadow-md transition-shadow" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider">{label}</span>
        {Icon && <Icon className={`w-4 h-4 ${toneCls}`} />}
      </div>
      <div className={`text-[28px] font-semibold leading-none ${toneCls} tabular-nums`}>
        {value}{suffix && <span className="text-base font-normal text-neutral-400 ml-1">{suffix}</span>}
      </div>
      <div className="text-[11px] text-neutral-400 mt-2">{hint}</div>
    </div>
  );
};

const BUCKET_META = [
  { key: 'on_time_or_early', label: 'On time / early', color: '#10B981' },
  { key: 'late_1_3',         label: '1–3 days late',    color: '#F59E0B' },
  { key: 'late_4_7',         label: '4–7 days late',    color: '#F97316' },
  { key: 'late_gt_7',        label: '> 7 days late',    color: '#EF4444' },
];

const DelayHistogram = ({ buckets, total }) => {
  const max = Math.max(1, ...Object.values(buckets || {}));
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4" data-testid="delay-histogram">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[13px] font-semibold text-[#1a1a2e]">Delay distribution</div>
          <div className="text-[11px] text-neutral-500 mt-0.5">Among {total} tasks completed in window</div>
        </div>
      </div>
      <div className="space-y-2.5">
        {BUCKET_META.map((b) => {
          const n = buckets?.[b.key] ?? 0;
          const pct = total > 0 ? Math.round((n / total) * 100) : 0;
          return (
            <div key={b.key} className="flex items-center gap-3 text-[12px]">
              <div className="w-32 inline-flex items-center gap-2 text-neutral-700">
                <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: b.color }} />
                {b.label}
              </div>
              <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${(n / max) * 100}%`, background: b.color }} />
              </div>
              <div className="w-20 text-right text-neutral-600 tabular-nums">
                {n} <span className="text-neutral-400">({pct}%)</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PriorityPill = ({ priority }) => {
  const colors = {
    Urgent: 'bg-red-50 text-red-700 border-red-200',
    High:   'bg-amber-50 text-amber-700 border-amber-200',
    Medium: 'bg-blue-50 text-blue-700 border-blue-200',
    Low:    'bg-neutral-50 text-neutral-600 border-neutral-200',
  };
  return (
    <span className={`inline-flex px-1.5 py-0.5 text-[10px] rounded border ${colors[priority] || colors.Low}`}>
      {priority || '—'}
    </span>
  );
};

const DaysLate = ({ d }) => {
  if (d <= 0) return <span className="text-neutral-500">—</span>;
  const tone =
    d > 7 ? 'text-red-700 font-semibold' :
    d > 3 ? 'text-orange-600 font-medium' :
    'text-amber-600';
  return (
    <span className={tone} data-testid={`days-late-${d}`}>
      {d}d
    </span>
  );
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch {
    return iso.slice(0, 10);
  }
};

const DeadlinesTab = ({ filters }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const params = useMemo(() => buildParams(filters), [filters]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/api/admin/reports/tasks/deadlines`, { headers: auth(), params })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => !cancelled && toast.error(e.response?.data?.detail || 'Failed to load deadlines'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params]);

  if (loading && !data) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="bg-white border border-neutral-200 rounded-xl p-4 animate-pulse h-[110px]" />
        ))}
      </div>
    );
  }

  const t = data?.tiles || {};
  const buckets = data?.delay_buckets || {};
  const overdue = data?.overdue || [];

  return (
    <div className="space-y-4" data-testid="deadlines-tab">
      {/* Row 1 — SLA & on-time tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile
          icon={ShieldCheck} label="SLA adherence" value={`${t.sla_adherence_rate ?? 0}%`}
          hint={`${t.on_time ?? 0} / ${t.completed ?? 0} completed on time`}
          tone={t.sla_adherence_rate >= 90 ? 'emerald' : t.sla_adherence_rate >= 75 ? 'amber' : 'red'}
          testid="deadlines-tile-sla"
        />
        <Tile
          icon={Target} label="On-time rate" value={`${t.on_time_rate ?? 0}%`}
          hint="completed on or before due date"
          tone={t.on_time_rate >= 80 ? 'emerald' : t.on_time_rate >= 60 ? 'amber' : 'red'}
          testid="deadlines-tile-on-time-rate"
        />
        <Tile
          icon={CalendarCheck2} label="Ahead of deadline" value={t.ahead_of_deadline ?? 0}
          hint={t.avg_ahead_days > 0 ? `avg ${t.avg_ahead_days}d early` : 'no early completions'}
          tone="emerald" testid="deadlines-tile-ahead"
        />
        <Tile
          icon={TimerOff} label="Deadline misses" value={t.late ?? 0}
          hint={`${t.miss_rate ?? 0}% of completed`}
          tone={t.late === 0 ? 'emerald' : t.miss_rate <= 20 ? 'amber' : 'red'}
          testid="deadlines-tile-misses"
        />
      </div>

      {/* Row 2 — Overdue (current state) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile
          icon={AlertTriangle} label="Overdue (now)" value={t.overdue_count ?? 0}
          hint={`${t.overdue_rate ?? 0}% of ${t.total_active ?? 0} active`}
          tone={t.overdue_count === 0 ? 'emerald' : 'red'}
          testid="deadlines-tile-overdue"
        />
        <Tile
          icon={Clock} label="Avg delay" value={t.avg_delay_days ?? 0} suffix="d"
          hint="among late-completed tasks"
          tone={t.avg_delay_days <= 1 ? 'emerald' : t.avg_delay_days <= 3 ? 'amber' : 'red'}
          testid="deadlines-tile-avg-delay"
        />
        <Tile
          icon={CalendarX} label="Total active" value={t.total_active ?? 0}
          hint="not-yet-completed" tone="blue"
          testid="deadlines-tile-active"
        />
        <Tile
          icon={Target} label="Completed (window)" value={t.completed ?? 0}
          hint="basis for SLA / on-time math" tone="neutral"
          testid="deadlines-tile-completed"
        />
      </div>

      {/* Row 3 — Delay histogram */}
      <DelayHistogram buckets={buckets} total={t.completed ?? 0} />

      {/* Row 4 — Overdue task list */}
      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden" data-testid="overdue-list">
        <div className="px-4 py-3 border-b border-neutral-100 flex items-center justify-between">
          <div>
            <div className="text-[13px] font-semibold text-[#1a1a2e]">Overdue tasks · top {overdue.length}</div>
            <div className="text-[11px] text-neutral-500 mt-0.5">Active tasks past their due date — sorted by days late</div>
          </div>
          {overdue.length > 0 && (
            <span className="text-[10px] font-medium text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">
              {t.overdue_count} total overdue
            </span>
          )}
        </div>
        {overdue.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50">
                <tr className="text-left text-[11px] font-medium text-neutral-500 uppercase tracking-wider">
                  <th className="px-4 py-2.5">Title</th>
                  <th className="px-4 py-2.5">Client</th>
                  <th className="px-4 py-2.5">Assignee</th>
                  <th className="px-4 py-2.5">Due</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Priority</th>
                  <th className="px-4 py-2.5 text-right">Days late</th>
                </tr>
              </thead>
              <tbody>
                {overdue.map((r) => (
                  <tr key={r.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                    <td className="px-4 py-2.5 text-neutral-800 max-w-[280px] truncate" title={r.title}>{r.title}</td>
                    <td className="px-4 py-2.5 text-neutral-600">{r.client}</td>
                    <td className="px-4 py-2.5 text-neutral-600">{r.assignee}</td>
                    <td className="px-4 py-2.5 text-neutral-600 tabular-nums">{fmtDate(r.due_date)}</td>
                    <td className="px-4 py-2.5"><span className="text-[10px] text-neutral-500">{r.status}</span></td>
                    <td className="px-4 py-2.5"><PriorityPill priority={r.priority} /></td>
                    <td className="px-4 py-2.5 text-right tabular-nums"><DaysLate d={r.days_late} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-10 text-center text-sm text-emerald-700">
            🎉 No overdue tasks — all active work is within deadline.
          </div>
        )}
      </div>
    </div>
  );
};

export default DeadlinesTab;
